import React, { useCallback, useEffect, useRef, useState } from "react";
import { IconChevronLeft, IconChevronRight, IconX, IconDownload } from "@tabler/icons-react";
import clsx from "clsx";
import { GalleryItem, useSite } from "../../../store";
import { getOriginalFileUrl } from "../../../services";
import { Button, CircleLoader } from "../../components";
import { useTranslation } from "react-i18next";
import "./GalleryLightbox.scss";

interface GalleryLightboxProps {
  items: GalleryItem[];
  currentIndex: number;
  isOpen: boolean;
  hasMore: boolean;
  loading: boolean;
  onClose: () => void;
  onIndexChange: (index: number) => void;
  onLoadMore: () => void;
}

const MIN_SWIPE_DISTANCE = 50;
const TAP_MOVE_TOLERANCE = 10;
const MAX_SCALE = 4;
const DOUBLE_TAP_SCALE = 2.5;
const DOUBLE_TAP_MS = 300;
const PREFETCH_THRESHOLD = 5;

type GestureMode = "none" | "swipe" | "pan" | "pinch";
type Point = { x: number; y: number };

export const GalleryLightbox = ({
  items,
  currentIndex,
  isOpen,
  hasMore,
  loading: isLoadingMore,
  onClose,
  onIndexChange,
  onLoadMore,
}: GalleryLightboxProps) => {
  const { t } = useTranslation();
  const { siteId, allowSingleImageDownload } = useSite();
  const currentItem = items[currentIndex];

  const [originalFile, setOriginalFile] = useState<{ url: string; type: 'image' | 'video'; name: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [mediaLoading, setMediaLoading] = useState(true);
  const [pendingNext, setPendingNext] = useState(false);

  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState<Point>({ x: 0, y: 0 });
  const [isGesturing, setIsGesturing] = useState(false);

  const contentRef = useRef<HTMLDivElement | null>(null);
  const gestureMode = useRef<GestureMode>("none");
  const pinchStartDist = useRef<number | null>(null);
  const pinchStartScale = useRef(1);
  const panStart = useRef<Point | null>(null);
  const panStartTranslate = useRef<Point>({ x: 0, y: 0 });
  const swipeStartX = useRef<number | null>(null);
  const swipeCurrentX = useRef<number | null>(null);
  const lastTapTime = useRef(0);
  const mousePanning = useRef(false);
  const mouseStart = useRef<Point>({ x: 0, y: 0 });
  const mouseStartTranslate = useRef<Point>({ x: 0, y: 0 });

  const isVideo = originalFile?.type === "video";

  const resetZoom = useCallback(() => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
    setIsGesturing(false);
  }, []);

  const clampTranslate = useCallback((x: number, y: number, s: number): Point => {
    const el = contentRef.current;
    if (!el) return { x, y };
    const rect = el.getBoundingClientRect();
    const maxX = (rect.width * (s - 1)) / 2;
    const maxY = (rect.height * (s - 1)) / 2;
    return {
      x: Math.max(-maxX, Math.min(maxX, x)),
      y: Math.max(-maxY, Math.min(maxY, y)),
    };
  }, []);

  const goNext = useCallback(() => {
    if (currentIndex < items.length - 1) {
      onIndexChange(currentIndex + 1);
    } else if (hasMore) {
      setPendingNext(true);
      onLoadMore();
    }
  }, [currentIndex, items.length, hasMore, onIndexChange, onLoadMore]);

  const goPrev = useCallback(() => {
    if (currentIndex > 0) {
      onIndexChange(currentIndex - 1);
    }
  }, [currentIndex, onIndexChange]);

  // Fetch the original file for the current item.
  useEffect(() => {
    if (currentItem && isOpen) {
      setLoading(true);
      setMediaLoading(true);
      setOriginalFile(null);
      resetZoom();
      getOriginalFileUrl(currentItem.name, siteId)
        .then(result => {
          setOriginalFile(result);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [currentItem, isOpen, siteId, resetZoom]);

  // Keyboard navigation.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "ArrowLeft") {
        goPrev();
      } else if (e.key === "ArrowRight") {
        goNext();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose, goPrev, goNext]);

  // Prefetch the next batch a few items before reaching the end.
  useEffect(() => {
    if (
      isOpen &&
      hasMore &&
      !isLoadingMore &&
      currentIndex >= items.length - PREFETCH_THRESHOLD
    ) {
      onLoadMore();
    }
  }, [isOpen, hasMore, isLoadingMore, currentIndex, items.length, onLoadMore]);

  // Auto-advance once an awaited batch has arrived.
  useEffect(() => {
    if (!pendingNext) return;
    if (currentIndex < items.length - 1) {
      onIndexChange(currentIndex + 1);
      setPendingNext(false);
    } else if (!isLoadingMore) {
      setPendingNext(false);
    }
  }, [pendingNext, currentIndex, items.length, isLoadingMore, onIndexChange]);

  // Reset transient state when the lightbox closes.
  useEffect(() => {
    if (!isOpen) {
      setPendingNext(false);
    }
  }, [isOpen]);

  if (!isOpen || !currentItem) return null;

  const handlePrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    goPrev();
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    goNext();
  };

  const triggerDownload = (href: string, fileName: string) => {
    const link = document.createElement('a');
    link.href = href;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!originalFile) return;
    const { url, type, name } = originalFile;

    // Videos can be large, so download them directly via the storage URL
    // instead of buffering the whole file into memory as a blob.
    if (type === 'video') {
      triggerDownload(url, name);
      return;
    }

    // The HTML5 `download` attribute is ignored for cross-origin URLs, so
    // browsers open images inline instead of saving them. Fetch the file
    // and download it from a same-origin blob URL to force a real download.
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Download failed: ${response.status}`);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      triggerDownload(objectUrl, name);
      // Defer revocation so the browser can start reading the blob first.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (error) {
      console.error('Failed to download file:', error);
      // Fall back to opening the file in a new tab so the user can still
      // save it manually if the blob download is blocked (e.g. by CORS).
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const getTouchDistance = (touches: React.TouchList) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.hypot(dx, dy);
  };

  const zoomTo = (targetScale: number, focusX?: number, focusY?: number) => {
    setIsGesturing(false);
    if (targetScale <= 1) {
      resetZoom();
      return;
    }
    let offsetX = 0;
    let offsetY = 0;
    const el = contentRef.current;
    if (el && focusX !== undefined && focusY !== undefined) {
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      offsetX = (cx - focusX) * (targetScale - 1);
      offsetY = (cy - focusY) * (targetScale - 1);
    }
    setScale(targetScale);
    setTranslate(clampTranslate(offsetX, offsetY, targetScale));
  };

  const toggleZoomAt = (focusX?: number, focusY?: number) => {
    if (scale > 1) {
      resetZoom();
    } else {
      zoomTo(DOUBLE_TAP_SCALE, focusX, focusY);
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!isVideo && e.touches.length === 2) {
      gestureMode.current = "pinch";
      pinchStartDist.current = getTouchDistance(e.touches);
      pinchStartScale.current = scale;
      setIsGesturing(true);
      return;
    }

    if (!isVideo && scale > 1) {
      gestureMode.current = "pan";
      panStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      panStartTranslate.current = translate;
      setIsGesturing(true);
      return;
    }

    gestureMode.current = "swipe";
    swipeStartX.current = e.touches[0].clientX;
    swipeCurrentX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (gestureMode.current === "pinch" && e.touches.length === 2 && pinchStartDist.current) {
      const dist = getTouchDistance(e.touches);
      const next = Math.min(
        MAX_SCALE,
        Math.max(1, pinchStartScale.current * (dist / pinchStartDist.current))
      );
      setScale(next);
      if (next === 1) {
        setTranslate({ x: 0, y: 0 });
      }
    } else if (gestureMode.current === "pan" && panStart.current) {
      const dx = e.touches[0].clientX - panStart.current.x;
      const dy = e.touches[0].clientY - panStart.current.y;
      setTranslate(
        clampTranslate(
          panStartTranslate.current.x + dx,
          panStartTranslate.current.y + dy,
          scale
        )
      );
    } else if (gestureMode.current === "swipe") {
      swipeCurrentX.current = e.touches[0].clientX;
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const mode = gestureMode.current;

    if (mode === "pinch") {
      if (e.touches.length < 2) {
        if (scale <= 1.02) {
          resetZoom();
        } else {
          setIsGesturing(false);
        }
        gestureMode.current = "none";
        pinchStartDist.current = null;
      }
      return;
    }

    if (mode === "pan") {
      setIsGesturing(false);
      panStart.current = null;
      gestureMode.current = "none";
      return;
    }

    if (mode === "swipe") {
      const start = swipeStartX.current;
      const end = swipeCurrentX.current;
      gestureMode.current = "none";
      swipeStartX.current = null;
      swipeCurrentX.current = null;

      if (start === null || end === null) return;
      const distance = start - end;

      if (Math.abs(distance) < TAP_MOVE_TOLERANCE) {
        const now = Date.now();
        if (now - lastTapTime.current < DOUBLE_TAP_MS) {
          lastTapTime.current = 0;
          if (!isVideo) {
            const point = e.changedTouches[0];
            toggleZoomAt(point?.clientX, point?.clientY);
          }
        } else {
          lastTapTime.current = now;
        }
        return;
      }

      if (scale === 1) {
        if (distance > MIN_SWIPE_DISTANCE) {
          goNext();
        } else if (distance < -MIN_SWIPE_DISTANCE) {
          goPrev();
        }
      }
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (scale <= 1 || isVideo) return;
    mousePanning.current = true;
    mouseStart.current = { x: e.clientX, y: e.clientY };
    mouseStartTranslate.current = translate;
    setIsGesturing(true);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!mousePanning.current) return;
    const dx = e.clientX - mouseStart.current.x;
    const dy = e.clientY - mouseStart.current.y;
    setTranslate(
      clampTranslate(
        mouseStartTranslate.current.x + dx,
        mouseStartTranslate.current.y + dy,
        scale
      )
    );
  };

  const endMousePan = () => {
    if (!mousePanning.current) return;
    mousePanning.current = false;
    setIsGesturing(false);
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isVideo) return;
    toggleZoomAt(e.clientX, e.clientY);
  };

  const isZoomed = scale > 1;
  const showNext = currentIndex < items.length - 1 || hasMore;
  const mediaStyle: React.CSSProperties = {
    transform: `translate3d(${translate.x}px, ${translate.y}px, 0) scale(${scale})`,
    transition: isGesturing ? "none" : "transform 0.25s ease, opacity 0.3s ease",
  };
  const contentStyle: React.CSSProperties = {
    cursor: isZoomed ? (mousePanning.current ? "grabbing" : "grab") : "default",
  };

  return (
    <div className={clsx("lightbox", { open: isOpen })} onClick={onClose}>
      <div className="controls">
        <div className="top-controls">
          {allowSingleImageDownload && (
            <div className="download">
              <Button
                appearance="muted"
                icon={<IconDownload />}
                iconOnly
                disabled={loading || !originalFile}
                onClick={handleDownload}
              />
            </div>
          )}
          <div className="close">
            <Button
              appearance="muted"
              icon={<IconX />}
              iconOnly
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
            />
          </div>
        </div>

        {!isZoomed && currentIndex > 0 && (
          <div className="prev">
            <Button
              appearance="muted"
              icon={<IconChevronLeft />}
              iconOnly
              onClick={handlePrev}
            />
          </div>
        )}

        {!isZoomed && showNext && (
          <div className="next">
            {pendingNext && isLoadingMore ? (
              <span className="nav-spinner" aria-label={t("gallery.loadingMore")} />
            ) : (
              <Button
                appearance="muted"
                icon={<IconChevronRight />}
                iconOnly
                onClick={handleNext}
              />
            )}
          </div>
        )}
      </div>

      <div
        ref={contentRef}
        className="content"
        style={contentStyle}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={handleDoubleClick}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={endMousePan}
        onMouseLeave={endMousePan}
      >
        <div className={clsx("loader", { hidden: !loading && !mediaLoading })}>
          <CircleLoader />
        </div>
        {!loading && originalFile && (
          originalFile.type === 'video' ? (
            <video
              src={originalFile.url}
              controls
              autoPlay
              playsInline
              loop
              onLoadedData={() => setMediaLoading(false)}
              className={clsx(mediaLoading && "loading")}
            />
          ) : (
            <img
              src={originalFile.url}
              alt={currentItem.name}
              draggable={false}
              onLoad={() => setMediaLoading(false)}
              className={clsx(mediaLoading && "loading")}
              style={mediaStyle}
            />
          )
        )}
        {!loading && !originalFile && (
          <img
            src={currentItem.downloadUrl}
            alt={currentItem.name}
            draggable={false}
            onLoad={() => setMediaLoading(false)}
            className={clsx(mediaLoading && "loading")}
            style={mediaStyle}
          />
        )}
      </div>
    </div>
  );
};
